<template>
  <div class="app-evn-wrapper">
    <slot name="txtBefore"></slot>
    <slot name="txtHint"></slot>
    <EvnCosts>
      <template v-for="slot in possibleChildSlotsFor('EvnCosts')" v-slot:[`${slot.child}`]>
        <slot :name="`${slot.parent}`"></slot>
      </template>
    </EvnCosts>
    <div class="date-picker-wrapper">
      <div class="content">
        <div class="txt-current-customer">
          <slot name="txtMsisdnBefore">Alle Verbindungen für die Rufnummer </slot>{{msisdn}}<slot name="txtMsisdnAfter"> anzeigen</slot>
        </div>
        <b-form-group class="date-group">
          <b-row>
            <b-col class="date-picker">
              <b-form-group :label-for="'fromData' + _uid"
                            label-cols="auto"
                            label="von:">
                <DatePicker
                  :id="'fromData' + _uid"
                  @input="$v.filterStartDate.$touch"
                  :valid="isValid('filterStartDate')"
                  :date.sync="filterStartDate"
                  name="startDate"
                  :disabled="loading"
                  :calendar-options="{min: minDate }"
                ></DatePicker>
              </b-form-group>
            </b-col>
            <b-col class="date-picker">
              <b-form-group :label-for="'toData' + _uid"
                            label-cols="auto"
                            label="bis:">
                <DatePicker
                  :id="'toData' + _uid"
                  @input="$v.filterEndDate.$touch"
                  :valid="isValid('filterEndDate')"
                  :date.sync="filterEndDate"
                  name="endDate"
                  :disabled="loading"
                ></DatePicker>
              </b-form-group>
            </b-col>
          </b-row>
        </b-form-group>
        <Stepper
          @next="refreshQuery()"
          :loading-next="loading"
          :show-prev="false"
        >
          <template #btnNext>
            <slot name="btnNext">Aktualisieren</slot>
          </template>
        </Stepper>
      </div>
    </div>
<!--    inside Germany-->
    <div class="evn-headline"><slot name="insideHeadline">Verbindungen innerhalb Deutschlands</slot></div>
    <ItemisedBillingGroupList :group="germanyIn" v-if="sectionIsNotEmpty(germanyIn)" :extra-data="extraData">
      <template v-for="slot in possibleChildSlotsFor('GroupInside')" v-slot:[`${slot.child}`]>
        <slot :name="`${slot.parent}`"></slot>
      </template>
    </ItemisedBillingGroupList>
    <p v-else><slot name="txtGermanyInGroupEmpty">Für den gewählten Zeitraum liegen keine EVN-Einträge in diesem Bereich vor.</slot></p>
<!--    outside germany-->
    <div class="evn-headline"><slot name="insideHeadline">Verbindungen von Deutschland ins Ausland</slot></div>
    <ItemisedBillingGroupList :group="germanyOut" v-if="sectionIsNotEmpty(germanyOut)" :extra-data="extraData">
      <template v-for="slot in possibleChildSlotsFor('GroupOutside')" v-slot:[`${slot.child}`]>
        <slot :name="`${slot.parent}`"></slot>
      </template>
    </ItemisedBillingGroupList>
    <p v-else><slot name="txtGermanyOutGroupEmpty">Für den gewählten Zeitraum liegen keine EVN-Einträge in diesem Bereich vor.</slot></p>
    <!--    abroad-->
    <div class="evn-headline"><slot name="insideHeadline">Verbindungen im Ausland (Roaming)</slot></div>
    <ItemisedBillingGroupList :group="abroad" v-if="sectionIsNotEmpty(abroad)" :extra-data="extraData">
      <template v-for="slot in possibleChildSlotsFor('GroupAbroad')" v-slot:[`${slot.child}`]>
        <slot :name="`${slot.parent}`"></slot>
      </template>
    </ItemisedBillingGroupList>
    <p v-else><slot name="txtAbroadGroupEmpty">Für den gewählten Zeitraum liegen keine EVN-Einträge in diesem Bereich vor.</slot></p>
    <!--    other-->
    <div class="evn-headline"><slot name="insideHeadline">Sonstiges</slot></div>
    <ItemisedBillingGroupList :group="other" :group-type="'other'" v-if="sectionIsNotEmpty(other)" :extra-data="extraData">
      <template v-for="slot in possibleChildSlotsFor('GroupOther')" v-slot:[`${slot.child}`]>
        <slot :name="`${slot.parent}`"></slot>
      </template>
    </ItemisedBillingGroupList>
    <p v-else><slot name="txtOtherGroupEmpty">Für den gewählten Zeitraum liegen keine EVN-Einträge in diesem Bereich vor.</slot></p>
    <!--    topUp-->
    <div class="evn-headline"><slot name="insideHeadline">Aufladungen und Gutschriften</slot></div>
    <ItemisedBillingGroupList :group="topUps" :group-type="'topUpAndVoucher'" v-if="sectionIsNotEmpty(topUps)" :extra-data="extraData">
      <template v-for="slot in possibleChildSlotsFor('GroupTopUps')" v-slot:[`${slot.child}`]>
        <slot :name="`${slot.parent}`"></slot>
      </template>
    </ItemisedBillingGroupList>
    <p v-else><slot name="txtTopUpsGroupEmpty">Für den gewählten Zeitraum liegen keine EVN-Einträge in diesem Bereich vor.</slot></p>
    <slot name="txtAfter"></slot>
  </div>
</template>

<script>

import transaction from '@/mixins/transaction'
import slotProxy from '@/mixins/slotProxy'
import gql from 'graphql-tag'
import EvnCosts from './EvnCosts.vue'
import { wrapQuery } from '../../../../../helper/wrapGQLQuery'
import ItemisedBillingGroupList from './ItemisedBillingGroupList.vue'
import preFilledInput from '../../../../../mixins/preFilledInput'
import { validationMixin } from 'vuelidate'
import isValid from '../../../../../mixins/isValid'
import { required } from 'vuelidate/lib/validators'
import { isSameOrAfter, validDate, validDateFormat, validDatePeriod } from '../../../../../validators/date'
import Stepper from '../../../../generic/Stepper.vue'
import DatePicker from '../../../../generic/DatePicker.vue'
import extraData from '../../../../../mixins/extraData'

const _Today = new Date()
const _FirstOfMonth = new Date()
_FirstOfMonth.setDate(1)

export default {
  name: 'EvnWrapper',
  data () {
    return {
      currentCustomer: null,
      loading: false
    }
  },
  components: {
    ItemisedBillingGroupList,
    Stepper,
    DatePicker,
    EvnCosts
  },
  computed: {
    msisdn: function () {
      return this.currentCustomer?.contract?.msisdn
    },
    minDate () {
      const mindate = new Date()
      mindate.setDate(mindate.getDate() - 80)
      return mindate
    },
    today () {
      return this.$config.calendar.formatter.dateFormatter.format(_Today)
    },
    firstOfMonth () {
      return this.$config.calendar.formatter.dateFormatter.format(_FirstOfMonth)
    },
    germanyIn: function () {
      return this.currentCustomer?.sortedItemisedBills?.connectionsInGermany
    },
    germanyOut: function () {
      return this.currentCustomer?.sortedItemisedBills?.connectionsFromGermany
    },
    abroad: function () {
      return this.currentCustomer?.sortedItemisedBills?.connectionsAbroad
    },
    other: function () {
      return this.currentCustomer?.sortedItemisedBills?.other
    },
    topUps: function () {
      return this.currentCustomer?.sortedItemisedBills?.topUps
    }
  },
  methods: {
    refreshQuery () {
      this.loading = true
      this.$apollo.queries.currentCustomer.refetch({
        filter: this.getFilter()
      }).then(() => {
        this.loading = false
      }).catch(() => {
        this.loading = false
      })
    },
    sectionIsNotEmpty (section) {
      let notEmpty = false
      for (const group in section) {
        if (typeof section[group] === 'object' && section[group].length !== 0) {
          notEmpty = true
        }
      }
      return notEmpty
    },
    getFilter () {
      return {
        from: this.filterStartDate,
        to: this.filterEndDate
      }
    }
  },
  mixins: [
    transaction('itemisedBills'),
    slotProxy(),
    preFilledInput('filterStartDate', 'firstOfMonth'),
    preFilledInput('filterEndDate', 'today'),
    validationMixin,
    isValid,
    extraData()
  ],
  apollo: {
    currentCustomer: function () {
      const me = this
      me.loading = true
      return wrapQuery(
        gql`query itemisedBills ($filter: ItemisedBillFilterInput){
          currentCustomer {
            contract {
              msisdn
            }
            sortedItemisedBills (filter: $filter){
              connectionsInGermany {
                phoneCalls {
                    actionDate
                    destination
                    details
                    duration
                    id
                    value
                    typeKey
                    groupKey
                }
                freePhoneCalls{
                    actionDate
                    destination
                    details
                    duration
                    id
                    value
                    typeKey
                    groupKey
                }
                freeSms {
                    actionDate
                    destination
                    details
                    duration
                    id
                    value
                    typeKey
                    groupKey
                }
                freeMms {
                    actionDate
                    destination
                    details
                    duration
                    id
                    value
                    typeKey
                    groupKey
                }
                sms {
                    actionDate
                    destination
                    details
                    duration
                    id
                    value
                    typeKey
                    groupKey
                }
                mms {
                    actionDate
                    destination
                    details
                    duration
                    id
                    value
                    typeKey
                    groupKey
                }
              }
              connectionsFromGermany {
                phoneCalls {
                    actionDate
                    destination
                    details
                    duration
                    id
                    value
                    typeKey
                    groupKey
                }
                freePhoneCalls{
                    actionDate
                    destination
                    details
                    duration
                    id
                    value
                    typeKey
                    groupKey
                }
                freeSms {
                    actionDate
                    destination
                    details
                    duration
                    id
                    value
                    typeKey
                    groupKey
                }
                freeMms {
                    actionDate
                    destination
                    details
                    duration
                    id
                    value
                    typeKey
                    groupKey
                }
                sms {
                    actionDate
                    destination
                    details
                    duration
                    id
                    value
                    typeKey
                    groupKey
                }
                mms {
                    actionDate
                    destination
                    details
                    duration
                    id
                    value
                    typeKey
                    groupKey
                }
              }
              connectionsAbroad {
                 phoneCalls {
                    actionDate
                    destination
                    details
                    duration
                    id
                    value
                    typeKey
                    groupKey
                }
                freePhoneCalls{
                    actionDate
                    destination
                    details
                    duration
                    id
                    value
                    typeKey
                    groupKey
                }
                freeSms {
                    actionDate
                    destination
                    details
                    duration
                    id
                    value
                    typeKey
                    groupKey
                }
                freeMms {
                    actionDate
                    destination
                    details
                    duration
                    id
                    value
                    typeKey
                    groupKey
                }
                sms {
                    actionDate
                    destination
                    details
                    duration
                    id
                    value
                    typeKey
                    groupKey
                }
                mms {
                    actionDate
                    destination
                    details
                    duration
                    id
                    value
                    typeKey
                    groupKey
                }
              }
              other {
                other {
                    actionDate
                    destination
                    details
                    duration
                    id
                    value
                    typeKey
                    groupKey
                }
                serviceCalls {
                    actionDate
                    destination
                    details
                    duration
                    id
                    value
                    typeKey
                    groupKey
                }
                freeServiceCalls {
                    actionDate
                    destination
                    details
                    duration
                    id
                    value
                    typeKey
                    groupKey
                }
              }
              topUps {
                topUp {
                    actionDate
                    destination
                    details
                    duration
                    id
                    value
                    typeKey
                    groupKey
                }
                voucher {
                    actionDate
                    destination
                    details
                    duration
                    id
                    value
                    typeKey
                    groupKey
                }
              }
            }
          }
        }`, this, 'currentCustomer', {
          filter: this.getFilter()
        },
        () => {
          me.loading = false
        }
      )
    }
  },
  validations () {
    return {
      filterStartDate: {
        validDate,
        validDatePeriod: validDatePeriod(80),
        validDateFormat,
        required
      },
      filterEndDate: {
        validDate,
        validDatePeriod: validDatePeriod(80),
        validDateFormat,
        afterStartDate: (endDate, vm) => {
          const startDate = vm.filterStartDate
          return isSameOrAfter(startDate, endDate)
        },
        required
      }
    }
  }
}
</script>
